// admin-panel/src/pages/Settings.jsx
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { settingsApi } from '../lib/api';
import { Save, Settings2, Globe, Phone, Palette, Search } from 'lucide-react';
import toast from 'react-hot-toast';

const GROUPS = [
  { key: 'general', label: 'General',  icon: Settings2 },
  { key: 'contact', label: 'Contact',  icon: Phone },
  { key: 'seo',     label: 'SEO',      icon: Globe },
  { key: 'social',  label: 'Social',   icon: Globe },
  { key: 'business',label: 'Business', icon: Palette },
];

export default function Settings() {
  const qc = useQueryClient();
  const [activeGroup, setActiveGroup] = useState('general');
  const [values, setValues] = useState({});

  const { data: settings = [], isLoading } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: settingsApi.list,
  });

  const list = settings?.data || settings || [];

  useEffect(() => {
    if (list.length > 0) {
      const init = {};
      list.forEach(s => { init[s.key_name] = s.value || ''; });
      setValues(init);
    }
  }, [list.length]);

  const saveMut = useMutation({
    mutationFn: (data) => settingsApi.bulkUpdate({ settings: data }),
    onSuccess: () => { qc.invalidateQueries(['admin-settings']); toast.success('Settings saved!'); },
    onError: (e) => toast.error(e.message || 'Save failed'),
  });

  const groupedSettings = list.reduce((acc, s) => {
    const g = s.group_name || 'general';
    if (!acc[g]) acc[g] = [];
    acc[g].push(s);
    return acc;
  }, {});

  const currentGroup = groupedSettings[activeGroup] || [];

  function handleSave() {
    const payload = Object.entries(values).map(([key_name, value]) => ({ key_name, value }));
    saveMut.mutate(payload);
  }

  const INPUT = "w-full bg-[#1A1A1A] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30";

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">Settings</h1>
          <p className="text-sm text-[#555] mt-0.5">Configure site settings, SEO, and contact info</p>
        </div>
        <button onClick={handleSave} disabled={saveMut.isPending}
          className="flex items-center gap-2 bg-gradient-to-r from-[#8B5E3C] to-[#D4AF37] text-white text-sm font-semibold px-5 py-2.5 rounded-xl hover:opacity-90 disabled:opacity-50 transition-all">
          <Save size={15} /> {saveMut.isPending ? 'Saving…' : 'Save All'}
        </button>
      </div>

      <div className="grid lg:grid-cols-4 gap-5">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-[#151515] border border-white/5 rounded-xl p-2 space-y-0.5">
            {GROUPS.map(({ key, label, icon: Icon }) => (
              <button key={key} onClick={() => setActiveGroup(key)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${activeGroup === key ? 'bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20' : 'text-[#888] hover:text-white hover:bg-white/5'}`}>
                <Icon size={15} className="shrink-0" />
                {label}
                {groupedSettings[key]?.length > 0 && (
                  <span className="ml-auto text-xs text-[#555]">{groupedSettings[key].length}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-3">
          <div className="bg-[#151515] border border-white/5 rounded-xl p-5 space-y-4">
            <h3 className="text-sm font-semibold text-white capitalize mb-2">{activeGroup} Settings</h3>

            {isLoading ? (
              Array(4).fill(0).map((_, i) => <div key={i} className="h-12 bg-white/5 rounded-xl animate-pulse" />)
            ) : currentGroup.length === 0 ? (
              <div className="text-center py-8 text-[#555] text-sm">
                <Settings2 size={32} className="mx-auto mb-2 text-[#333]" />
                No settings in this group yet.
              </div>
            ) : (
              currentGroup.map((setting) => (
                <div key={setting.key_name}>
                  <label className="block text-xs text-[#555] mb-1.5">
                    {setting.label || setting.key_name}
                    {setting.description && <span className="text-[#444] ml-2">— {setting.description}</span>}
                  </label>

                  {setting.type === 'textarea' ? (
                    <textarea
                      value={values[setting.key_name] || ''}
                      onChange={e => setValues(v => ({ ...v, [setting.key_name]: e.target.value }))}
                      rows={3}
                      placeholder={`Enter ${setting.label || setting.key_name}`}
                      className={`${INPUT} resize-none`}
                    />
                  ) : setting.type === 'boolean' ? (
                    <label className="flex items-center gap-3 cursor-pointer">
                      <div
                        onClick={() => setValues(v => ({ ...v, [setting.key_name]: v[setting.key_name] === '1' ? '0' : '1' }))}
                        className={`w-11 h-6 rounded-full relative transition-all ${values[setting.key_name] === '1' ? 'bg-[#D4AF37]' : 'bg-white/10'}`}>
                        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${values[setting.key_name] === '1' ? 'left-6' : 'left-1'}`} />
                      </div>
                      <span className="text-sm text-[#888]">{values[setting.key_name] === '1' ? 'Enabled' : 'Disabled'}</span>
                    </label>
                  ) : setting.type === 'color' ? (
                    <div className="flex items-center gap-3">
                      <input type="color" value={values[setting.key_name] || '#D4AF37'}
                        onChange={e => setValues(v => ({ ...v, [setting.key_name]: e.target.value }))}
                        className="w-12 h-10 rounded-lg border-0 bg-transparent cursor-pointer" />
                      <input type="text" value={values[setting.key_name] || ''}
                        onChange={e => setValues(v => ({ ...v, [setting.key_name]: e.target.value }))}
                        placeholder="#D4AF37" className="flex-1 bg-[#1A1A1A] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#D4AF37]/30" />
                    </div>
                  ) : (
                    <input
                      type={setting.type === 'email' ? 'email' : setting.type === 'url' ? 'url' : setting.type === 'number' ? 'number' : 'text'}
                      value={values[setting.key_name] || ''}
                      onChange={e => setValues(v => ({ ...v, [setting.key_name]: e.target.value }))}
                      placeholder={`Enter ${setting.label || setting.key_name}`}
                      className={INPUT}
                    />
                  )}
                </div>
              ))
            )}

            {currentGroup.length > 0 && (
              <div className="pt-2 border-t border-white/5">
                <button onClick={handleSave} disabled={saveMut.isPending}
                  className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#8B5E3C] to-[#D4AF37] text-white text-sm font-semibold rounded-xl hover:opacity-90 disabled:opacity-50 transition-all">
                  <Save size={14} /> {saveMut.isPending ? 'Saving…' : 'Save Settings'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
